import { Box, Divider, Grid } from "@mui/material";
import { EditEntry } from "../../../entriesAPI";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import DayOfWeekDate from "../../../../../common/components/functional/DayOfWeekDate/DayOfWeekDate";
import StartEndTimeFields from "../../Shared/StartEndTimeFields";
import LunchBreakFields from "../../Shared/LunchBreakFields";
import { getWorkTimeDuration } from "../../../../../app/common";
import { fetchAssignmentById } from "../../../../talents/talentsSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect } from "react";

interface Props {
  entry: EditEntry | null;
  setEntry: (val: EditEntry) => void;
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
}

const BasicInfo = ({ entry, setEntry, inputErrors, setInputErrors }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );

  useEffect(() => {
    if (entry?.assignmentId) {
      dispatch(fetchAssignmentById(entry.assignmentId)).then((action) => {
        if (isFulfilled(action)) {
          // For long assignment entries, check if default lunch break exists
          if (
            !action.payload.isShift &&
            action.payload.details.defaultLunchBreak
          ) {
            const { defaultLunchBreak } = action.payload.details;
            setEntry({
              ...entry,
              defaultLunchBreak,
            });
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, entry?.assignmentId, setEntry]);

  const updateShiftTime = (updatedData: { start: number; end: number }) => {
    const updatedEntry = {
      ...entry,
      start: updatedData.start,
      end: updatedData.end,
      lunchBreak: entry?.lunchBreak,
    } as EditEntry;

    setEntry(updatedEntry);
  };

  return (
    entry && (
      <Grid
        item
        xs={6}
        margin={0}
        padding={0}
        sx={{
          paddingRight: "15px",
        }}
      >
        <Divider
          sx={{
            marginTop: "16px",
            marginBottom: "16px",
          }}
        />

        <DayOfWeekDate start={entry.start} backgroundColor="#FF505A" />

        <Box
          sx={{
            color: "rgba(0, 0, 0, 0.87)",
            fontWeight: "700",
            marginTop: "16px",
            marginBottom: "4px",
          }}
        >
          {`${entry.workLocationName} - ${translatedWorkTitles[entry?.workTitleId]}`}
        </Box>

        <Box>{entry.customerCostCenterName}</Box>

        <StartEndTimeFields
          id={"absenceView_shift"}
          setInputErrors={setInputErrors}
          inputErrors={inputErrors}
          disabled={false}
          requireValue={true}
          start={entry.start}
          end={entry.end}
          plannedStart={entry.plannedStart}
          update={updateShiftTime}
        />

        <LunchBreakFields
          entry={entry}
          setEntry={setEntry}
          id={"absenceView_lunchBreak"}
          inputErrors={inputErrors}
          setInputErrors={setInputErrors}
        />

        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            marginTop: "16px",
          }}
        >
          {`${t("hourEntries:absenceHours")}: ${getWorkTimeDuration(new Date(entry.end), new Date(entry.start), entry.lunchBreak?.duration)}`}
        </Box>
      </Grid>
    )
  );
};

export default BasicInfo;
